import React, { useState } from "react";
import { Box, Text, Button } from "@puzzle/ve";
import { Menu, Switch } from "@puzzle/ui";
import { Dots, Settings2 } from "@puzzle/icons";
import { useUpdateMetricCardPreferencesMutation } from "./graphql.generated";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import {
  baseContainerStyle,
  menuContainerStyle,
  menuFooterStyle,
  menuHeaderStyle,
  menuItemContainerStyle,
  menuItemDescriptionStyle,
} from "./statCardsHeader.css";
import { vars } from "@puzzle/theme";
import { MetricCard, MetricCardPreferenceInput } from "graphql/types";
import { MetricCardStatus } from "./Dashboard";
import Analytics from "lib/analytics/analytics";
import { useAnalyticsHelper } from "lib/analytics/analyticsHelper";

export const METRIC_CARD_NAMES = {
  [MetricCard.Cash]: "Cash",
  [MetricCard.TotalExpenses]: "Expenses",
  [MetricCard.Revenue]: "Revenue",
  [MetricCard.GrossProfitMargin]: "Gross Profit Margin",
  [MetricCard.AccountsPayable]: "Accounts Payable",
  [MetricCard.AccountsReceivable]: "Accounts Receivable",
  [MetricCard.Spend]: "Spend",
  [MetricCard.NetBurn]: "Net Burn",
  [MetricCard.NetBurnMoM]: "Net Burn MoM",
  [MetricCard.BurnMtd]: "Burn MTD",
  [MetricCard.Runway]: "Runway Run Rate",
  [MetricCard.NetProfitMargin]: "Net Profit Margin",
};

const MENU_ITEM_BORDER = `1px solid ${vars.colors["outline-neutral-subdue"]}`;
const MENU_ITEM_DRAGGED_OPACITY = 0.4;

export type StatCardsHeaderProps = {
  metricCards: MetricCard[];
  metricCardStatus?: MetricCardStatus;
  onChangeStatus: (value: MetricCardStatus) => void;
  onChangeOrder: (cards: MetricCard[]) => void;
};

export const StatCardsHeader = ({
  metricCards,
  metricCardStatus = {} as MetricCardStatus,
  onChangeStatus,
  onChangeOrder,
}: StatCardsHeaderProps) => {
  const { company } = useActiveCompany<true>();
  const [updatePreferences] = useUpdateMetricCardPreferencesMutation();
  const { identifyUser } = useAnalyticsHelper();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [closestIndex, setClosestIndex] = useState<number | null>(null);

  const handleDragStart = (index: number, e: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (index: number, e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setClosestIndex(index);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedIndex(null);
  };

  const handleDrop = (index: number) => {
    if (draggedIndex === null || draggedIndex === index) return;

    const updatedCards = [...metricCards];
    const [moved] = updatedCards.splice(draggedIndex, 1);
    updatedCards.splice(index, 0, moved);
    setDraggedIndex(null);
    setIsDragging(false);
    onChangeOrder(updatedCards);

    const metricCardPreferences = updatedCards.map((metricCard) => {
      return {
        metricCard,
        enabled: metricCardStatus[metricCard],
      };
    });

    updatePreferences({
      variables: {
        input: {
          companyId: company.id,
          metricCardPreferences,
        },
      },
    });
    identifyUserAndFireAnalyticsEvent(metricCardPreferences);
  };

  const handleSwitchItem = (item: MetricCard, enabled: boolean) => {
    const metricCardPreferences = metricCards.map((metricCard) => {
      if (item === metricCard) {
        return { metricCard, enabled };
      }
      return {
        metricCard,
        enabled: metricCardStatus[metricCard],
      };
    });

    onChangeStatus({ ...metricCardStatus, [item]: enabled });
    updatePreferences({
      variables: {
        input: {
          companyId: company.id,
          metricCardPreferences,
        },
      },
    });
    identifyUserAndFireAnalyticsEvent(metricCardPreferences);
  };

  const handleSwitchAllItems = () => {
    const metricCardPreferences = metricCards.map((metricCard) => ({
      metricCard,
      enabled: true,
    }));
    const updatedPreferences = { ...metricCardStatus };
    metricCards.forEach((metricCard) => {
      updatedPreferences[metricCard] = true;
    });

    onChangeStatus(updatedPreferences);
    updatePreferences({
      variables: {
        input: {
          companyId: company.id,
          metricCardPreferences,
        },
      },
    });
    identifyUserAndFireAnalyticsEvent(metricCardPreferences);
  };

  const handleCustomizeOpen = () => {
    Analytics.dashboardCustomizeClicked();
  };

  const identifyUserAndFireAnalyticsEvent = (preferences: MetricCardPreferenceInput[]) => {
    const cards = preferences
      .filter((card) => card.enabled)
      .map((card) => METRIC_CARD_NAMES[card.metricCard]);
    const dashboardCardConfiguration = cards.join(", ");

    identifyUser({
      dashboardCardConfiguration,
    });

    Analytics.dashboardCardConfigurationUpdated({
      dashboardCardConfiguration,
    });
  };

  return (
    <Box className={baseContainerStyle}>
      <Text variant="headingS" weight="normal">
        Financial Insights
      </Text>

      <Menu
        className={menuContainerStyle}
        onOpenChange={(open) => open && handleCustomizeOpen()}
        trigger={
          <Button variant="secondaryAlt" size="small" prefixElement={<Settings2 size={16} />}>
            Customize
          </Button>
        }
      >
        <Box className={menuHeaderStyle}>
          <Text
            variant="bodyXXS"
            weight="bold"
            color="neutral-base"
            style={{ letterSpacing: "1px" }}
          >
            CUSTOMIZE VIEW
          </Text>
        </Box>
        <Menu.Group>
          {metricCards.map((metricCard, index) => {
            const isCurrentDragged = isDragging && index === draggedIndex;
            const isClosestItem = isDragging && index === closestIndex;
            const showBorderTop = isClosestItem && closestIndex < (draggedIndex || 0);
            const showBorderBottom = isClosestItem && closestIndex > (draggedIndex || 0);

            return (
              <Menu.Item
                key={metricCard}
                className={menuItemContainerStyle}
                onClick={(e) => e.preventDefault()}
                draggable
                onDragEnd={handleDragEnd}
                onDragStart={(e) => handleDragStart(index, e)}
                onDragOver={(e) => handleDragOver(index, e)}
                onDrop={() => handleDrop(index)}
                css={{
                  borderTop: showBorderTop ? MENU_ITEM_BORDER : "unset",
                  borderBottom: showBorderBottom ? MENU_ITEM_BORDER : "unset",
                  opacity: isCurrentDragged ? MENU_ITEM_DRAGGED_OPACITY : "unset",
                }}
              >
                <Box>
                  <Dots rotate={90} viewBox="0 3 10 10" />
                  <Dots rotate={90} viewBox="0 0 10 5" />
                </Box>
                <Text
                  variant="body"
                  weight="normal"
                  color="neutral-base"
                  className={menuItemDescriptionStyle}
                >
                  {METRIC_CARD_NAMES[metricCard]}
                </Text>
                <Switch
                  switchCss={{
                    height: 16,
                  }}
                  checked={metricCardStatus[metricCard]}
                  onCheckedChange={(checked) => handleSwitchItem(metricCard, checked)}
                />
              </Menu.Item>
            );
          })}
        </Menu.Group>

        <Menu.Separator css={{ backgroundColor: vars.colors["outline-neutral-mostSubdue"] }} />

        <Box className={menuFooterStyle}>
          <Button variant="minimal" size="mini" onClick={handleSwitchAllItems}>
            Show All
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};
